/** Onboarding step 2: connect a coding agent over MCP (Claude Code, Codex, Cursor) or the CLI, with an optional new API key */

import { useId, useRef, useState, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Key } from 'lucide-react'
import { apiClient } from '../../lib/api-client'
import { AGENT_CONNECT_TABS, connectSnippet, type AgentConnectTab } from '../../lib/agent-connect'
import { useT } from '../../i18n'
import { cn } from '../../lib/utils'
import { DOCS_URL } from '../shell/nav-config'
import { CopySnippet } from '../setup/copy-snippet'
import { buttonSecondaryClass, cardClass, errorTextClass, hintClass } from '../ui/styles'

const TAB_LABEL: Record<AgentConnectTab, string> = {
  'claude-code': 'Claude Code',
  codex: 'Codex',
  cursor: 'Cursor',
  cli: 'CLI',
}

/** Scopes an agent needs to push Sources, read the brief, and write wiki pages */
export const AGENT_KEY_SCOPES = ['doc:read', 'doc:search', 'doc:create', 'doc:update']

interface CreatedKey {
  id: string
  key: string
}

export function ConnectAgentStep({ project, projectName }: { project: string; projectName: string }) {
  const t = useT()
  const id = useId()
  const qc = useQueryClient()
  const [tab, setTab] = useState<AgentConnectTab>('claude-code')
  const tabRefs = useRef<Partial<Record<AgentConnectTab, HTMLButtonElement | null>>>({})

  const createKey = useMutation({
    mutationFn: () => apiClient.post<CreatedKey>('/api/keys', { name: t('onboarding.agent.keyName'), scopes: AGENT_KEY_SCOPES }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  })
  const apiKey = createKey.data?.key

  const onTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const index = AGENT_CONNECT_TABS.indexOf(tab)
    const next =
      e.key === 'ArrowRight' ? AGENT_CONNECT_TABS[(index + 1) % AGENT_CONNECT_TABS.length]
        : e.key === 'ArrowLeft' ? AGENT_CONNECT_TABS[(index - 1 + AGENT_CONNECT_TABS.length) % AGENT_CONNECT_TABS.length]
          : e.key === 'Home' ? AGENT_CONNECT_TABS[0]
            : e.key === 'End' ? AGENT_CONNECT_TABS[AGENT_CONNECT_TABS.length - 1]
              : null
    if (!next) return
    e.preventDefault()
    setTab(next)
    tabRefs.current[next]?.focus()
  }

  const docsHref = tab === 'cli' ? `${DOCS_URL}/04-cli-reference/` : `${DOCS_URL}/05-mcp-server/`

  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t('onboarding.agent.intro', { project: projectName })}</p>

      <div className={cn(cardClass, 'space-y-3 p-4')}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold text-fg">
              <Key className="h-4 w-4 text-accent-text" aria-hidden="true" />
              {t('onboarding.agent.keyTitle')}
            </p>
            <p className={cn(hintClass, 'mt-0.5')}>{t('onboarding.agent.keyHint')}</p>
          </div>
          {!apiKey && (
            <button type="button" onClick={() => createKey.mutate()} disabled={createKey.isPending} className={cn(buttonSecondaryClass, 'shrink-0')}>
              {createKey.isPending ? t('onboarding.agent.creatingKey') : t('onboarding.agent.createKey')}
            </button>
          )}
        </div>
        {apiKey && (
          <>
            <p role="status" className="text-sm text-success">{t('onboarding.agent.keyCreated')}</p>
            <CopySnippet code={apiKey} label={t('onboarding.agent.keyTitle')} />
          </>
        )}
        {createKey.isError && (
          <p role="alert" className={errorTextClass}>
            {t('onboarding.agent.keyFailed', { message: createKey.error instanceof Error ? createKey.error.message : String(createKey.error) })}
          </p>
        )}
        <Link to="/settings/api-keys" className="inline-block text-sm text-accent-text hover:underline">
          {t('onboarding.agent.manageKeys')}
        </Link>
      </div>

      <div className="space-y-3">
        <div role="tablist" aria-label={t('onboarding.agent.tabs')} className="flex flex-wrap gap-1 border-b border-border">
          {AGENT_CONNECT_TABS.map((value) => {
            const selected = value === tab
            return (
              <button
                key={value}
                ref={(el) => { tabRefs.current[value] = el }}
                type="button"
                role="tab"
                id={`${id}-tab-${value}`}
                aria-selected={selected}
                aria-controls={`${id}-panel`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setTab(value)}
                onKeyDown={onTabKeyDown}
                className={cn(
                  '-mb-px min-h-9 cursor-pointer border-b-2 px-3 text-sm font-medium',
                  selected ? 'border-accent text-fg' : 'border-transparent text-fg-muted hover:text-fg',
                )}
              >
                {TAB_LABEL[value]}
              </button>
            )
          })}
        </div>
        <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${tab}`} className="space-y-2">
          <p className="text-sm text-fg-muted">{t(`onboarding.agent.${tab}`)}</p>
          <CopySnippet code={connectSnippet(tab, { apiKey, project })} label={TAB_LABEL[tab]} />
          <a href={docsHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline">
            {t('onboarding.agent.docs')}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  )
}
